import React, { useState } from 'react';
import { View, TextInput } from 'react-native-web';
import CustomText from '../../../../components/atoms/CustomText';
import Button from '../../../../components/common/Button';
import { colors, spacing } from '../../../../utils/styling';
import { useAddClientNote, useClientNotes } from '../../../../lib/clientNotes';

// F16 notes box (ClickUp 86bah7dqn). McKayla 2026-09-20: visible to the advisor and
// others within their firm, NOT to the client. The client portal never renders this
// tab and the backend refuses clients.
export default function NotesTab({ sfAccount }: { sfAccount: string }) {
  const { data: notes, isLoading, error } = useClientNotes(sfAccount);
  const addNote = useAddClientNote(sfAccount);
  const [draft, setDraft] = useState('');

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    await addNote.mutateAsync(body);
    setDraft('');
  };

  return (
    <View style={styles.content}>
      <View style={styles.card}>
        <CustomText style={styles.sectionTitle}>Notes</CustomText>
        <CustomText style={styles.hint}>
          Visible to you and others at your firm. Clients never see these.
        </CustomText>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          multiline
          numberOfLines={4}
          maxLength={5000}
          placeholder="Add a note about this client..."
          placeholderTextColor={colors.gray[500]}
          style={styles.input}
        />
        <View style={{ alignItems: 'flex-end' }}>
          <Button
            onPress={save}
            label="Save note"
            loading={addNote.isPending}
            disabled={addNote.isPending || !draft.trim()}
          />
        </View>
        {addNote.isError && (
          <CustomText style={styles.error}>Couldn't save that note. Please try again.</CustomText>
        )}
      </View>

      <View style={{ ...styles.card, marginTop: spacing.lg }}>
        {isLoading ? (
          <CustomText style={styles.hint}>Loading notes...</CustomText>
        ) : error ? (
          <CustomText style={styles.error}>Couldn't load notes.</CustomText>
        ) : !notes || notes.length === 0 ? (
          <CustomText style={styles.hint}>No notes yet.</CustomText>
        ) : (
          notes.map((n) => (
            <View key={n.id} style={styles.note}>
              <CustomText style={styles.meta}>
                {n.author_name || 'Team member'} · {new Date(n.created_at).toLocaleString()}
              </CustomText>
              <CustomText style={styles.body}>{n.body}</CustomText>
            </View>
          ))
        )}
      </View>
    </View>
  );
}

const styles = {
  content: {
    padding: spacing.xl,
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  card: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: spacing.xl,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: colors.dark },
  hint: { marginTop: spacing.xs, fontSize: 14, color: colors.gray[600] },
  input: {
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.gray[200],
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 96,
    fontFamily: 'Nunito',
  },
  error: { marginTop: spacing.sm, fontSize: 14, color: colors.danger },
  note: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray[200],
  },
  meta: { fontSize: 12, color: colors.gray[500], marginBottom: spacing.xs },
  body: { fontSize: 14, color: colors.gray[700], whiteSpace: 'pre-wrap' },
} as const;
